import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Button, Popover, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, TextField,
  Snackbar, Alert
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { getCurrentUser } from '../../services/auth';
import { api } from '../../api';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const SLOT_H = 16; // фіксована висота 15-хв слота (px) — компактно й без розтягування рядків
const DAY_SLOT_H = 22; // висота слота в перегляді одного дня (просторіше, читабельно)
const SHOW_HOURS_TOGGLE = false; // кнопку «весь день/робочі години» сховано (функціонал лишається)

export default function DashboardCalendarView() {
  const { t, i18n } = useTranslation();
  const DAYS = [
    t('dashboard.mon'), t('dashboard.tue'), t('dashboard.wed'),
    t('dashboard.thu'), t('dashboard.fri'), t('dashboard.sat'), t('dashboard.sun'),
  ];
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [timeOff, setTimeOff] = useState([]); // закриті блоки/вихідні
  const [workHours, setWorkHours] = useState([]); // тижневі робочі години майстра
  const [dayOverrides, setDayOverrides] = useState([]); // per-date «відкриті» години
  const [showAllHours, setShowAllHours] = useState(false); // показати весь день (0–24) для ручного запису поза графіком
  // Переглянутий тиждень зберігаємо в sessionStorage — щоб при поверненні з
  // деталей запису (кнопка «Назад») лишатись на тому ж тижні/місяці, а не стрибати
  // на поточний. «Сьогодні» скидає на 0.
  const [weekOffset, setWeekOffset] = useState(() => {
    const v = Number(sessionStorage.getItem('dashWeekOffset'));
    return Number.isFinite(v) ? v : 0;
  });
  const [slideDir, setSlideDir] = useState(0); // напрямок анімації зміни тижня: -1 назад, 1 вперед, 0 fade
  const goWeek = (delta) => { setSlideDir(delta > 0 ? 1 : -1); setWeekOffset((p) => p + delta); };
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [slotMenu, setSlotMenu] = useState(null); // { anchor, date, time }
  const [blockMenu, setBlockMenu] = useState(null); // { anchor, block }
  const [closeDlg, setCloseDlg] = useState(null); // { date, start, end, reason }
  const [openDlg, setOpenDlg] = useState(null); // { date, start, end, existingId } — «відкрити» години
  const [editBlockDlg, setEditBlockDlg] = useState(null); // { id, date, start, end, reason } — редагувати блок
  const [warn, setWarn] = useState(''); // попап-попередження (конфлікт із бронню тощо)
  const [dayView, setDayView] = useState(null); // дата (YYYY-MM-DD) для перегляду одного дня
  const [justCreated, setJustCreated] = useState(null); // тост після створення запису
  const [linkCopied, setLinkCopied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Після редіректу зі створення запису — показати тост із лінком-підтвердженням
  useEffect(() => {
    if (location.state?.resetWeek) {
      // Явний перехід у «Панель» — скидаємо на поточний тиждень (навіть якщо вже тут)
      setWeekOffset(0);
      sessionStorage.setItem('dashWeekOffset', '0');
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.created) {
      setJustCreated(location.state.created);
      // прибрати state з історії, щоб тост не зʼявлявся при оновленні/назад
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Запам'ятовуємо переглянутий тиждень (щоб «Назад» повертав на нього)
  useEffect(() => {
    sessionStorage.setItem('dashWeekOffset', String(weekOffset));
  }, [weekOffset]);

  const today = dayjs();
  const startOfWeek = today.startOf('isoWeek').add(weekOffset, 'week');
  const days = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));

  const reload = useCallback(async () => {
    try {
      const current = await getCurrentUser();
      if (!current || !current.id) {
        navigate('/login');
        return;
      }
      const [bookingList, userList, sched] = await Promise.all([
        api.getBookings(),
        api.getUsers(),
        api.getSchedule().catch(() => ({ timeOff: [] })),
      ]);
      const mine = bookingList
        .filter(b => b.masterId === current.id)
        .map(b => {
          const u = userList.find(u => u.id === b.userId);
          const totalDuration = Array.isArray(b.services)
            ? b.services.reduce((acc, s) => acc + (s.duration || 0), 0)
            : 0;
          const serviceNames = Array.isArray(b.services)
            ? b.services.map(s => pickServiceName(s, i18n.language)).join(', ')
            : '';
          return {
            ...b,
            // Імʼя клієнта приходить із самої броні (join у бекенді); userList — запасний варіант
            userName: b.clientName || u?.name || t('dashboard.clientFallback'),
            servicesLabel: serviceNames,
            // Слід у календарі = час бронювання (block_duration, з буфером майстра),
            // а не час роботи. Бекенд уже зберігає його в duration_minutes.
            duration: b.durationMinutes ?? totalDuration,
          };
        });
      setBookings(mine);
      setUsers(userList);
      setTimeOff(sched.timeOff || []);
      setWorkHours(sched.workHours || []);
      setDayOverrides(sched.dayOverrides || []);
    } catch (e) {
      // Транзієнтна помилка (напр. фонове оновлення) — НЕ стираємо календар, лишаємо останні дані
      console.error('reload dashboard:', e?.message);
    }
  }, [navigate, i18n.language]);

  useEffect(() => { reload(); }, [reload]);

  // Живий календар: оновлюємо при поверненні у застосунок (foreground) і легким пулом,
  // щоб нові/змінені записи зʼявлялись без перезапуску PWA.
  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', reload);
    const id = setInterval(refreshIfVisible, 30000);
    return () => {
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', reload);
      clearInterval(id);
    };
  }, [reload]);

  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

  // Видимий діапазон годин = робочі години майстра (найраніший старт → найпізніший кінець
  // серед робочих днів тижня), розширений, щоб покрити будь-які закриті блоки/броні.
  const workingRows = workHours.filter(w => w.isWorking && w.startTime && w.endTime);
  let rangeStart = workingRows.length ? Math.min(...workingRows.map(w => toMin(w.startTime))) : 9 * 60;
  let rangeEnd = workingRows.length ? Math.max(...workingRows.map(w => toMin(w.endTime))) : 18 * 60;
  // Часткові закриті блоки (не цілі вихідні) мають бути видимі, навіть якщо поза робочими годинами
  for (const o of timeOff) {
    if (o.fullDay || !o.startTime || !o.endTime) continue;
    rangeStart = Math.min(rangeStart, toMin(o.startTime));
    rangeEnd = Math.max(rangeEnd, toMin(o.endTime));
  }
  // Відкриті на конкретну дату години теж мають бути видимі в діапазоні
  const ovByDate = new Map(dayOverrides.map(o => [o.date, o]));
  for (const o of dayOverrides) {
    rangeStart = Math.min(rangeStart, toMin(o.startTime));
    rangeEnd = Math.max(rangeEnd, toMin(o.endTime));
  }
  // За замовчуванням показуємо 07:00–22:00 (розширюємо, якщо броні/блоки/відкриті
  // години виходять за ці межі). «Весь день» (0–24) лишається у функціоналі —
  // кнопку сховано з UI (SHOW_HOURS_TOGGLE), але стан і логіка збережені.
  const DEFAULT_VIEW_START = 7 * 60;
  const DEFAULT_VIEW_END = 22 * 60;
  const startHour = showAllHours ? 0 : Math.floor(Math.min(rangeStart, DEFAULT_VIEW_START) / 60);
  const endHour = showAllHours ? 24 : Math.max(startHour + 1, Math.ceil(Math.max(rangeEnd, DEFAULT_VIEW_END) / 60));
  const slotCount = (endHour - startHour) * 4; // по 15 хв

  // Робоче вікно конкретного дня тижня (0=нд … 6=сб, як у бекенді)
  const whByWeekday = new Map(workHours.map(w => [w.weekday, w]));
  // Слот поза робочими годинами саме цього дня. ADDITIVE: слот робочий, якщо він
  // у тижневому вікні АБО у per-date «відкритому» вікні (override лише ДОДАЄ
  // години, не стирає решту графіка дня).
  const isOutsideWork = (d, timeLabel) => {
    const slot = toMin(timeLabel);
    const ov = ovByDate.get(d.format('YYYY-MM-DD'));
    if (ov && slot >= toMin(ov.startTime) && slot < toMin(ov.endTime)) return false;
    const w = whByWeekday.get(d.day());
    if (w && w.isWorking && w.startTime && w.endTime &&
        slot >= toMin(w.startTime) && slot < toMin(w.endTime)) return false;
    return true;
  };
  // Слот належить «відкритому» на дату вікну (для маркування в календарі)
  const isOpenedSlot = (d, timeLabel) => {
    const ov = ovByDate.get(d.format('YYYY-MM-DD'));
    if (!ov) return false;
    const slot = toMin(timeLabel);
    return slot >= toMin(ov.startTime) && slot < toMin(ov.endTime);
  };
  // Закритий інтервал (блок або цілий вихідний) на цей слот
  const getBlockAt = (dateStr, timeLabel) => {
    const slot = toMin(timeLabel);
    return timeOff.find(o =>
      o.date === dateStr &&
      (o.fullDay || (slot >= toMin(o.startTime) && slot < toMin(o.endTime)))
    );
  };

  // ---- CALENDAR LOGIC ----
  const getBookingAt = (dateStr, timeStr) => {
    // Повертає букінг, якщо цей слот входить у інтервал [start, end)
    const slotTime = dayjs(`${dateStr}T${timeStr}`);
    return bookings.find(b => {
      const start = dayjs(`${dayjs(b.date).format('YYYY-MM-DD')}T${b.time.slice(0,5)}`);
      const end = start.add(b.duration, 'minute');
      return (slotTime.isSame(start) || (slotTime.isAfter(start) && slotTime.isBefore(end)));
    });
  };

  const isStartOfBooking = (b, slotTime) => {
    const localDate = dayjs(b.date).format('YYYY-MM-DD');
    const start = dayjs(`${localDate}T${b.time.slice(0,5)}`);
    return slotTime.isSame(start);
  };

  const isEndOfBooking = (b, slotTime) => {
    const localDate = dayjs(b.date).format('YYYY-MM-DD');
    const start = dayjs(`${localDate}T${b.time.slice(0,5)}`);
    const end = start.add(b.duration, 'minute').subtract(15, 'minute');
    return slotTime.isSame(end);
  };

  const goToToday = () => { setSlideDir(0); setWeekOffset(0); };

  // Свайп вліво/вправо → наступний/попередній тиждень (мобільний)
  const touchStart = React.useRef(null);
  const onTouchStart = (e) => {
    const tch = e.touches[0];
    touchStart.current = { x: tch.clientX, y: tch.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const tch = e.changedTouches[0];
    const dx = tch.clientX - touchStart.current.x;
    const dy = tch.clientY - touchStart.current.y;
    touchStart.current = null;
    // Лише виражений горизонтальний свайп (не вертикальна прокрутка)
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      goWeek(dx < 0 ? 1 : -1);
    }
  };
  const handleCalendarOpen = (event) => setAnchorEl(event.currentTarget);
  const handleCalendarClose = () => setAnchorEl(null);
  const handleDateChange = (value) => {
    const diff = value.startOf('isoWeek').diff(today.startOf('isoWeek'), 'week');
    setSlideDir(Math.sign(diff - weekOffset));
    setWeekOffset(diff);
    setAnchorEl(null);
  };

  const handleBookingClick = (id) => {
    navigate(`/master/booking/${id}`);
  };

  // Меню по кліку на вільний слот
  const openSlotMenu = (e, dateStr, timeLabel) =>
    setSlotMenu({ anchor: e.currentTarget, date: dateStr, time: timeLabel });
  const createFromSlot = () => {
    if (slotMenu) navigate(`/master/create?date=${slotMenu.date}&time=${slotMenu.time}`);
    setSlotMenu(null);
  };
  const openCloseDialog = () => {
    if (!slotMenu) return;
    const end = dayjs(`${slotMenu.date}T${slotMenu.time}`).add(60, 'minute').format('HH:mm');
    setCloseDlg({ date: slotMenu.date, start: slotMenu.time, end, reason: '' });
    setSlotMenu(null);
  };
  const submitClose = async () => {
    try {
      await api.addTimeOff({
        date: closeDlg.date,
        startTime: closeDlg.start,
        endTime: closeDlg.end,
        reason: closeDlg.reason || undefined,
      });
      setCloseDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.closeTimeError'));
    }
  };

  // «Відкрити робочі години» на конкретну дату — ДОДАЄ вікно поверх графіка.
  // Префіл від клікнутого слота + 2 год за замовчуванням. Якщо на дату вже є
  // «відкрите» вікно — редагуємо його.
  const openOpenHoursDialog = () => {
    if (!slotMenu) return;
    const existing = dayOverrides.find(o => o.date === slotMenu.date);
    const defEnd = dayjs(`${slotMenu.date}T${slotMenu.time}`)
      .add(2, 'hour').format('HH:mm');
    setOpenDlg({
      date: slotMenu.date,
      start: existing?.startTime || slotMenu.time,
      end: existing?.endTime || (defEnd === '00:00' ? '23:59' : defEnd),
      existingId: existing?.id || null,
    });
    setSlotMenu(null);
  };
  const submitOpenHours = async () => {
    try {
      await api.setDayOverride({ date: openDlg.date, startTime: openDlg.start, endTime: openDlg.end });
      setOpenDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.openHoursError'));
    }
  };
  const removeOpenHours = async () => {
    if (!openDlg?.existingId) { setOpenDlg(null); return; }
    try {
      await api.deleteDayOverride(openDlg.existingId);
      setOpenDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.openHoursError'));
    }
  };

  // Меню по кліку на закритий блок
  const openBlockMenu = (e, block) => setBlockMenu({ anchor: e.currentTarget, block });
  const removeBlock = async () => {
    try {
      await api.deleteTimeOff(blockMenu.block.id);
      setBlockMenu(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.openTimeError'));
    }
  };
  // Редагувати закритий блок (лише частковий — не цілий вихідний)
  const openEditBlockDialog = () => {
    const b = blockMenu?.block;
    if (!b) return;
    if (b.fullDay) { setWarn(t('dashboard.fullDayNoEdit')); setBlockMenu(null); return; }
    setEditBlockDlg({ id: b.id, date: b.date, start: b.startTime, end: b.endTime, reason: b.reason || '' });
    setBlockMenu(null);
  };
  const submitEditBlock = async () => {
    try {
      await api.updateTimeOff(editBlockDlg.id, {
        startTime: editBlockDlg.start,
        endTime: editBlockDlg.end,
        reason: editBlockDlg.reason || undefined,
      });
      setEditBlockDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.closeTimeError'));
    }
  };

  const renderDay = (day, selectedDates, pickersDayProps) => {
    const isToday = day.isSame(today, 'date');
    const isInWeek = day.isSame(startOfWeek, 'week');
    return (
      <PickersDay
        {...pickersDayProps}
        selected={false}
        sx={{
          ...(isToday && {
            bgcolor: '#c9a24a',
            color: '#1a1a16',
            '&:hover': { bgcolor: '#977536' },
            fontWeight: 'bold'
          }),
          ...(isInWeek && !isToday && {
            bgcolor: '#c9a24a22'
          })
        }}
      />
    );
  };

  // --- Дані для перегляду одного дня (Feature: клік по дню) ---
  const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const dayViewTitle = dayView
    ? `${t('dashboard.' + WEEKDAY_KEYS[dayjs(dayView).day()])}, ${dayjs(dayView).format('DD.MM.YYYY')}`
    : '';
  const dayViewBookings = dayView
    ? bookings
        .filter(b => dayjs(b.date).format('YYYY-MM-DD') === dayView)
        .slice()
        .sort((a, b) => a.time.slice(0, 5).localeCompare(b.time.slice(0, 5)))
    : [];
  const dayViewBlocks = dayView ? timeOff.filter(o => o.date === dayView) : [];
  // Базова ціна запису (кастомна або сума послуг)
  const basePrice = (b) => {
    if (b.price != null) return Number(b.price);
    return Array.isArray(b.services)
      ? b.services.reduce((acc, s) => acc + Number(s.price || 0), 0)
      : 0;
  };
  // Фінальна ціна з урахуванням знижки по промокоду
  const effPrice = (b) => Math.max(0, basePrice(b) - Number(b.discountAmount || 0));

  // --- Керування графіком прямо з перегляду одного дня ---
  const dayIsOff = (dateStr) => timeOff.some(o => o.date === dateStr && o.fullDay);
  // Текст робочих годин дня: тижневе вікно + відкриті години (additive), або «Вихідний»
  const dayWorkText = (dateStr) => {
    if (dayIsOff(dateStr)) return t('dashboard.dayOff');
    const w = whByWeekday.get(dayjs(dateStr).day());
    const parts = [];
    if (w && w.isWorking && w.startTime && w.endTime) parts.push(`${w.startTime}–${w.endTime}`);
    const ov = ovByDate.get(dateStr);
    if (ov) parts.push(`${ov.startTime}–${ov.endTime}`);
    return parts.length ? parts.join(' · ') : t('dashboard.dayOff');
  };
  // Відкрити/редагувати години на весь день (префіл: наявне відкрите вікно або тижневе)
  const openHoursForDay = (dateStr) => {
    const existing = dayOverrides.find(o => o.date === dateStr);
    const w = whByWeekday.get(dayjs(dateStr).day());
    setOpenDlg({
      date: dateStr,
      start: existing?.startTime || w?.startTime || '09:00',
      end: existing?.endTime || w?.endTime || '18:00',
      existingId: existing?.id || null,
    });
  };
  const closeHoursForDay = (dateStr) => {
    setCloseDlg({ date: dateStr, start: '12:00', end: '13:00', reason: '' });
  };
  // Перемкнути «цілий день вихідний» (додати/прибрати). Бекенд відхилить, якщо є активні записи.
  const toggleDayOff = async (dateStr) => {
    try {
      const off = timeOff.find(o => o.date === dateStr && o.fullDay);
      if (off) await api.deleteTimeOff(off.id);
      else await api.addTimeOff({ date: dateStr }); // без часу = цілий день
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.closeTimeError'));
    }
  };
  const removeBlockById = async (id) => {
    try {
      await api.deleteTimeOff(id);
      setEditBlockDlg(null);
      await reload();
    } catch (e) {
      setWarn(e.message || t('dashboard.openTimeError'));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', px: 0 }}>
        <Box flex={1} sx={{ minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 2 }}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" onClick={() => goWeek(-1)}>←</Button>
              <Button variant="contained" size="small" onClick={() => goWeek(1)}>→</Button>
              <Button onClick={goToToday}>{t('dashboard.today')}</Button>
              {SHOW_HOURS_TOGGLE && (
                <Button
                  size="small"
                  variant={showAllHours ? 'contained' : 'outlined'}
                  onClick={() => setShowAllHours(v => !v)}
                >
                  {showAllHours ? t('dashboard.workHoursOnly') : t('dashboard.allDay')}
                </Button>
              )}
            </Stack>
            <Button onClick={handleCalendarOpen}>
              {startOfWeek.format('MMMM YYYY')}
            </Button>
          </Stack>

          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCalendarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <StaticDatePicker
              value={startOfWeek}
              onChange={handleDateChange}
              displayStaticWrapperAs="desktop"
              renderDay={renderDay}
              sx={{ bgcolor: '#faf7f2', p: 1 }}
              minDate={today}
              shouldDisableDate={date => date.isBefore(today, 'day')}
            />
          </Popover>

          <Box onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
               sx={{ overflowX: 'hidden', touchAction: 'pan-y' }}>
          <Box
            key={weekOffset}
            sx={{
              minWidth: 0,
              // Легка slide-fade анімація в напрямку зміни тижня — щоб зміна була помітна
              animation: `${slideDir < 0 ? 'wkPrev' : slideDir > 0 ? 'wkNext' : 'wkFade'} 0.24s ease-out`,
              '@keyframes wkNext': {
                from: { opacity: 0.35, transform: 'translateX(22px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
              '@keyframes wkPrev': {
                from: { opacity: 0.35, transform: 'translateX(-22px)' },
                to: { opacity: 1, transform: 'translateX(0)' },
              },
              '@keyframes wkFade': { from: { opacity: 0.4 }, to: { opacity: 1 } },
            }}
          >
          <Box sx={{ maxHeight: { xs: '68vh', md: '75vh' }, overflowY: 'auto', px: { xs: 0.25, md: 2 } }}>
            {/* Шапка з днями — липка (position: sticky): лишається зверху, поки скроляться години */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 3, bgcolor: '#faf7f2', display: 'grid', gridTemplateColumns: { xs: '34px repeat(7, minmax(0, 1fr))', md: '60px repeat(7, minmax(0, 1fr))' }, borderBottom: '1px solid rgba(28,29,23,0.18)' }}>
            <Box />
            {days.map((d, idx) => (
              <Box
                key={idx}
                onClick={() => setDayView(d.format('YYYY-MM-DD'))}
                sx={{
                  textAlign: 'center',
                  py: 1,
                  fontWeight: 'bold',
                  fontSize: { xs: '0.66rem', md: 'inherit' },
                  color: '#1a1a16',
                  cursor: 'pointer',
                  bgcolor: d.isSame(today, 'date') ? '#c9a24a' : 'transparent',
                  borderLeft: idx !== 0 ? '1px solid rgba(28,29,23,0.12)' : undefined,
                  transition: '0.15s',
                  '&:hover': { bgcolor: d.isSame(today, 'date') ? '#b8912f' : 'rgba(201,162,74,0.18)' }
                }}
              >
                {DAYS[idx]}<br />{d.format('DD.MM')}
              </Box>
            ))}
          </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '34px repeat(7, minmax(0, 1fr))', md: '60px repeat(7, minmax(0, 1fr))' } }}>
              {Array.from({ length: slotCount }, (_, i) => {
                const hour = startHour + Math.floor(i / 4);
                const minutes = (i % 4) * 15;
                const timeLabel = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

                return (
                  <React.Fragment key={i}>
                    <Box
                      sx={{
                        textAlign: 'right',
                        pr: { xs: 0.25, md: 0.75 },
                        pt: '2px',
                        height: SLOT_H,
                        boxSizing: 'border-box',
                        fontSize: { xs: 10, md: 12 },
                        lineHeight: 1,
                        color: '#57564d',
                        visibility: minutes === 0 ? 'visible' : 'hidden',
                        borderTop: minutes === 0 ? '1px solid rgba(28,29,23,0.12)' : 'none'
                      }}
                    >
                      {minutes === 0 ? timeLabel : ''}
                    </Box>
                    {days.map((d, dayIdx) => {
                      const dateStr = d.format('YYYY-MM-DD');
                      const slotTime = d.hour(hour).minute(minutes).second(0);
                      const booking = getBookingAt(dateStr, timeLabel);
                      const discounted = booking && Number(booking.discountAmount || 0) > 0;
                      const checkedOut = booking && booking.checkedOut;
                      const block = !booking && getBlockAt(dateStr, timeLabel);
                      const isStart = booking && isStartOfBooking(booking, slotTime);
                      const isEnd = booking && isEndOfBooking(booking, slotTime);
                      const isBlockStart = block && (
                        block.fullDay ? (hour === startHour && minutes === 0)
                          : toMin(timeLabel) === toMin(block.startTime)
                      );

                      const outside = !booking && !block && isOutsideWork(d, timeLabel);
                      // «Відкритий» майстром вручну слот поза тижневим графіком — позначка
                      const opened = !booking && !block && !outside && isOpenedSlot(d, timeLabel)
                        && !(whByWeekday.get(d.day())?.isWorking
                             && toMin(timeLabel) >= toMin(whByWeekday.get(d.day())?.startTime || '00:00')
                             && toMin(timeLabel) < toMin(whByWeekday.get(d.day())?.endTime || '00:00'));
                      const background =
                        booking && hoveredId === booking.id
                          ? '#977536'
                          : checkedOut
                          ? 'rgba(94,106,78,0.30)' // запис із чекаутом — оливковий «завершено»
                          : booking
                          ? 'rgba(201, 162, 74, 0.30)'
                          : block
                          ? 'rgba(28,29,23,0.10)'
                          : outside
                          ? '#eae7df'
                          : '#faf7f2';

                      // «+» на вільних робочих слотах; на позаробочих — прихований, з'являється на hover
                      const showPlus = !booking && !block && (minutes === 0 || minutes === 30);

                      return (
                        <Box
                          key={`${dateStr}-${timeLabel}`}
                          onClick={(e) =>
                            booking
                              ? handleBookingClick(booking.id)
                              : block
                              ? openBlockMenu(e, block)
                              : openSlotMenu(e, dateStr, timeLabel)
                          }
                          onMouseEnter={() => booking && setHoveredId(booking.id)}
                          onMouseLeave={() => setHoveredId(null)}
                          sx={{
                            px: 0.75,
                            py: '2px',
                            height: SLOT_H,
                            minWidth: 0,
                            boxSizing: 'border-box',
                            position: 'relative',
                            bgcolor: background,
                            color: block ? '#57564d' : booking ? '#1a1a16' : '#8a887f',
                            borderLeft: dayIdx !== 0 ? '0.5px solid rgba(28,29,23,0.12)' : undefined,
                            borderBottom: booking && isEnd ? '1px solid #c9a24a' : 'none',
                            cursor: 'pointer',
                            transition: '0.2s',
                            '&:hover .slot-plus-out': { opacity: 0.4 },
                            // Записи зі знижкою по промокоду — зелена смуга (замість золотої)
                            borderInlineStart: booking
                              ? (discounted ? '4px solid #3f8f6b' : '4px solid #c9a24a')
                              : block
                              ? '4px solid rgba(28,29,23,0.35)'
                              : opened
                              ? '4px solid rgba(63,143,107,0.55)'
                              : undefined,
                            // Пріоритет: початок броні > годинна лінія > півгодинна
                            borderTop:
                              booking && isStart ? '1px solid #c9a24a'
                              : minutes === 0 ? '1px solid rgba(28,29,23,0.12)'
                              : minutes === 30 ? '1px dashed rgba(28,29,23,0.12)'
                              : 'none',
                          }}
                        >
                          {booking && isStart ? (
                            // Оверлей на всю висоту броні: не розтягує рядок, зайве обрізається
                            <Box sx={{
                              position: 'absolute',
                              top: 0, left: '4px', right: '2px',
                              height: `${Math.max(1, Math.round((booking.duration || 15) / 15)) * SLOT_H - 2}px`,
                              overflow: 'hidden',
                              zIndex: 2,
                              px: 0.5,
                              display: 'flex',
                              alignItems: 'center', // вміст по центру блоку — без порожньої нижньої половини
                              pointerEvents: 'none',
                            }}>
                              {discounted && (
                                <Box sx={{
                                  position: 'absolute', top: 1, right: checkedOut ? 16 : 1, zIndex: 3,
                                  bgcolor: '#3f8f6b', color: '#fff', borderRadius: '3px',
                                  fontSize: '0.52rem', fontWeight: 700, lineHeight: 1,
                                  px: '3px', py: '1px',
                                }}>
                                  %
                                </Box>
                              )}
                              {checkedOut && (
                                <Box sx={{
                                  position: 'absolute', top: 1, right: 1, zIndex: 3,
                                  bgcolor: '#5e6a4e', color: '#fff', borderRadius: '3px',
                                  fontSize: '0.52rem', fontWeight: 700, lineHeight: 1,
                                  px: '3px', py: '1px',
                                }}>
                                  ✓
                                </Box>
                              )}
                              <Stack spacing={0.1} sx={{ width: '100%' }}>
                                <Typography variant="caption" fontWeight="bold" noWrap
                                  sx={{ lineHeight: 1.15, display: 'block' }}>
                                  {booking.userName}
                                </Typography>
                                <Typography variant="caption" noWrap
                                  sx={{ fontSize: '0.7rem', lineHeight: 1.1, display: 'block' }}>
                                  {booking.servicesLabel}
                                </Typography>
                                <Typography variant="caption" noWrap
                                  sx={{ fontSize: '0.62rem', lineHeight: 1.1, display: 'block',
                                        color: booking.termsAccepted ? 'success.main' : 'warning.dark' }}>
                                  {booking.termsAccepted ? t('dashboard.termsAccepted') : t('dashboard.termsPending')}
                                </Typography>
                              </Stack>
                            </Box>
                          ) : null}
                          {isBlockStart && (
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              noWrap
                              sx={{ display: 'block', maxWidth: '100%', fontSize: { xs: '0.6rem', md: '0.75rem' } }}
                            >
                              🔒 {block.fullDay ? t('dashboard.dayOff') : t('dashboard.closed')}
                              {block.reason ? ` · ${block.reason}` : ''}
                            </Typography>
                          )}
                          {showPlus && (
                            <Typography variant="caption"
                              className={outside ? 'slot-plus-out' : undefined}
                              sx={{ opacity: outside ? 0 : 0.35, fontSize: '0.7rem', lineHeight: 1,
                                    display: 'block', transition: 'opacity 0.15s' }}>+</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
          </Box>
          </Box>
        </Box>
      </Box>

      {/* Меню вільного слота */}
      <Menu open={Boolean(slotMenu)} anchorEl={slotMenu?.anchor} onClose={() => setSlotMenu(null)}>
        <MenuItem onClick={createFromSlot}>{t('dashboard.createBooking')}</MenuItem>
        <MenuItem onClick={openOpenHoursDialog}>{t('dashboard.openHours')}</MenuItem>
        <MenuItem onClick={openCloseDialog}>{t('dashboard.closeTime')}</MenuItem>
      </Menu>

      {/* Меню закритого блоку */}
      <Menu open={Boolean(blockMenu)} anchorEl={blockMenu?.anchor} onClose={() => setBlockMenu(null)}>
        {!blockMenu?.block?.fullDay && (
          <MenuItem onClick={openEditBlockDialog}>{t('dashboard.editBlock')}</MenuItem>
        )}
        <MenuItem onClick={removeBlock}>{t('dashboard.openRemoveClosure')}</MenuItem>
      </Menu>

      {/* Діалог закриття годин */}
      <Dialog open={Boolean(closeDlg)} onClose={() => setCloseDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dashboard.closeTimeDialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('dashboard.dateLabel')}: {closeDlg?.date}</Typography>
            <Stack direction="row" spacing={2}>
              <TextField type="time" label={t('dashboard.from')} value={closeDlg?.start || ''}
                onChange={(e) => setCloseDlg(c => ({ ...c, start: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
              <TextField type="time" label={t('dashboard.to')} value={closeDlg?.end || ''}
                onChange={(e) => setCloseDlg(c => ({ ...c, end: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
            </Stack>
            <TextField label={t('dashboard.reasonOptional')} value={closeDlg?.reason || ''}
              onChange={(e) => setCloseDlg(c => ({ ...c, reason: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCloseDlg(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submitClose}>{t('dashboard.closeTime')}</Button>
        </DialogActions>
      </Dialog>

      {/* Діалог «відкрити робочі години» на конкретну дату */}
      <Dialog open={Boolean(openDlg)} onClose={() => setOpenDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dashboard.openHoursTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.dateLabel')}: {openDlg?.date}
            </Typography>
            <Typography variant="caption" sx={{ color: '#57564d' }}>{t('dashboard.openHoursHint')}</Typography>
            <Stack direction="row" spacing={2}>
              <TextField type="time" label={t('dashboard.from')} value={openDlg?.start || ''}
                onChange={(e) => setOpenDlg(c => ({ ...c, start: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
              <TextField type="time" label={t('dashboard.to')} value={openDlg?.end || ''}
                onChange={(e) => setOpenDlg(c => ({ ...c, end: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <span>
            {openDlg?.existingId && (
              <Button color="error" onClick={removeOpenHours}>{t('dashboard.openHoursRemove')}</Button>
            )}
          </span>
          <span>
            <Button onClick={() => setOpenDlg(null)}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={submitOpenHours} sx={{ ml: 1 }}>{t('common.save')}</Button>
          </span>
        </DialogActions>
      </Dialog>

      {/* Діалог редагування закритого блоку */}
      <Dialog open={Boolean(editBlockDlg)} onClose={() => setEditBlockDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('dashboard.editBlockTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">{t('dashboard.dateLabel')}: {editBlockDlg?.date}</Typography>
            <Stack direction="row" spacing={2}>
              <TextField type="time" label={t('dashboard.from')} value={editBlockDlg?.start || ''}
                onChange={(e) => setEditBlockDlg(c => ({ ...c, start: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
              <TextField type="time" label={t('dashboard.to')} value={editBlockDlg?.end || ''}
                onChange={(e) => setEditBlockDlg(c => ({ ...c, end: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
            </Stack>
            <TextField label={t('dashboard.reasonOptional')} value={editBlockDlg?.reason || ''}
              onChange={(e) => setEditBlockDlg(c => ({ ...c, reason: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button color="error" onClick={() => removeBlockById(editBlockDlg.id)}>
            {t('dashboard.openRemoveClosure')}
          </Button>
          <span>
            <Button onClick={() => setEditBlockDlg(null)}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={submitEditBlock} sx={{ ml: 1 }}>{t('common.save')}</Button>
          </span>
        </DialogActions>
      </Dialog>

      <Dialog open={!!warn} onClose={() => setWarn('')} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>⚠️ {t('common.attention')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary' }}>{warn}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarn('')} variant="contained" autoFocus>OK</Button>
        </DialogActions>
      </Dialog>

      {/* --- Перегляд одного дня як календар: вільні + зайняті слоти (клік по даті) --- */}
      <Dialog open={!!dayView} onClose={() => setDayView(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: '#faf7f2', color: '#1a1a16', borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          🗓️ {dayViewTitle}
          <Typography variant="caption" sx={{ display: 'block', color: '#57564d', fontWeight: 400 }}>
            {t('dashboard.dayViewHint')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {dayView && (
            <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: '#f3efe7', border: '1px solid rgba(28,29,23,0.10)' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a16' }}>
                {t('dashboard.workingHours')}: {dayWorkText(dayView)}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" color="inherit" onClick={() => openHoursForDay(dayView)}>
                  {t('dashboard.openHours')}
                </Button>
                <Button size="small" variant="outlined" color="inherit" onClick={() => closeHoursForDay(dayView)}>
                  {t('dashboard.closeTime')}
                </Button>
                <Button size="small" variant="outlined" color="error" onClick={() => toggleDayOff(dayView)}>
                  {dayIsOff(dayView) ? t('dashboard.cancelDayOff') : t('dashboard.makeDayOff')}
                </Button>
              </Stack>
            </Box>
          )}
          {dayView && (
            <Box sx={{ position: 'relative', mt: 1 }}>
              {/* Фон: сітка 15-хв слотів. Вільні (не закриті) — клікабельні для створення */}
              {Array.from({ length: slotCount }, (_, i) => {
                const hour = startHour + Math.floor(i / 4);
                const minutes = (i % 4) * 15;
                const timeLabel = `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                const outside = isOutsideWork(dayjs(dayView), timeLabel);
                const block = getBlockAt(dayView, timeLabel);
                const isHour = minutes === 0;
                const free = !block;
                const rowBorder = isHour ? '1px solid rgba(28,29,23,0.18)' : '1px dashed rgba(28,29,23,0.06)';
                return (
                  <Box
                    key={i}
                    onClick={() => { if (free) { setDayView(null); navigate(`/master/create?date=${dayView}&time=${timeLabel}`); } }}
                    sx={{
                      display: 'grid', gridTemplateColumns: '52px 1fr', height: DAY_SLOT_H,
                      cursor: free ? 'pointer' : 'default',
                      '&:hover .dv-plus': { opacity: free ? 0.5 : 0 },
                    }}
                  >
                    <Box sx={{ pr: 1, textAlign: 'right', fontSize: '0.7rem', color: '#8a887f',
                               borderTop: rowBorder, lineHeight: `${DAY_SLOT_H}px`, boxSizing: 'border-box' }}>
                      {isHour ? timeLabel : ''}
                    </Box>
                    <Box sx={{ position: 'relative', borderTop: rowBorder, boxSizing: 'border-box',
                               bgcolor: block ? 'transparent' : outside ? '#efece6' : '#fbf9f5' }}>
                      <Typography className="dv-plus" sx={{
                        position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                        opacity: 0, transition: '0.15s', fontSize: '0.9rem', color: '#977536', fontWeight: 700
                      }}>＋</Typography>
                    </Box>
                  </Box>
                );
              })}

              {/* Закриті блоки / вихідні — сірі накладки з підписом */}
              {dayViewBlocks.map((o) => {
                const rangeStartMin = startHour * 60;
                const rangeEndMin = startHour * 60 + slotCount * 15;
                const bStart = o.fullDay ? rangeStartMin : toMin(o.startTime);
                const bEnd = o.fullDay ? rangeEndMin : toMin(o.endTime);
                const clStart = Math.max(bStart, rangeStartMin);
                const clEnd = Math.min(bEnd, rangeEndMin);
                if (clEnd <= clStart) return null;
                const top = ((clStart - rangeStartMin) / 15) * DAY_SLOT_H;
                const height = ((clEnd - clStart) / 15) * DAY_SLOT_H;
                return (
                  <Box key={`blk-${o.id}`}
                    onClick={() => {
                      if (o.fullDay) toggleDayOff(dayView);
                      else setEditBlockDlg({ id: o.id, date: o.date, start: o.startTime, end: o.endTime, reason: o.reason || '' });
                    }}
                    sx={{
                    position: 'absolute', left: '52px', right: 0, top, height,
                    bgcolor: 'rgba(28,29,23,0.10)', borderInlineStart: '4px solid rgba(28,29,23,0.35)',
                    display: 'flex', alignItems: 'center', px: 1, boxSizing: 'border-box', overflow: 'hidden',
                    cursor: 'pointer', transition: '0.15s', '&:hover': { bgcolor: 'rgba(28,29,23,0.16)' }
                  }}>
                    <Typography variant="caption" noWrap sx={{ color: '#57564d' }}>
                      🔒 {o.fullDay ? t('dashboard.dayOff') : t('dashboard.closed')}{o.reason ? ` · ${o.reason}` : ''}
                    </Typography>
                  </Box>
                );
              })}

              {/* Записи — накладки, клік → деталі (зелена смуга = знижка) */}
              {dayViewBookings.map((b) => {
                const start = b.time.slice(0, 5);
                const top = ((toMin(start) - startHour * 60) / 15) * DAY_SLOT_H;
                const height = Math.max(1, Number(b.duration || 0) / 15) * DAY_SLOT_H;
                const end = dayjs(`2000-01-01T${start}`).add(Number(b.duration || 0), 'minute').format('HH:mm');
                const disc = Number(b.discountAmount || 0) > 0;
                const co = !!b.checkedOut;
                return (
                  <Box key={b.id} onClick={() => { setDayView(null); handleBookingClick(b.id); }}
                    sx={{
                      position: 'absolute', left: '52px', right: 0, top, height, overflow: 'hidden',
                      bgcolor: co ? '#d3d9c4' : '#e7d7ad', borderInlineStart: `4px solid ${disc ? '#3f8f6b' : '#c9a24a'}`,
                      border: `1px solid ${co ? '#b9c2a3' : '#d9c48f'}`,
                      borderRadius: '2px', px: 1, py: '2px', cursor: 'pointer', boxSizing: 'border-box',
                      transition: '0.15s', '&:hover': { bgcolor: co ? '#c8d0b6' : '#e0cc98' }
                    }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ gap: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                        {co ? '✓ ' : ''}{start}–{end}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {disc && (
                          <Typography variant="caption" noWrap sx={{ color: '#8a887f', textDecoration: 'line-through', fontSize: '0.62rem' }}>
                            {formatMoney(basePrice(b))}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 700, color: disc ? '#3f8f6b' : '#5e6a4e' }} noWrap>{formatMoney(effPrice(b))}</Typography>
                      </Stack>
                    </Stack>
                    <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600, lineHeight: 1.15 }}>
                      {b.userName || b.clientName}
                    </Typography>
                    {b.servicesLabel && height >= DAY_SLOT_H * 3 && (
                      <Typography variant="caption" noWrap sx={{ display: 'block', color: '#57564d' }}>
                        {b.servicesLabel}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDayView(null)} variant="outlined" color="inherit">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Тост після створення запису (редірект у панель) */}
      <Snackbar
        open={!!justCreated}
        autoHideDuration={8000}
        onClose={() => setJustCreated(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setJustCreated(null)}
          sx={{ alignItems: 'center' }}
          action={
            justCreated?.confirmUrl ? (
              <Button color="inherit" size="small" onClick={() => {
                navigator.clipboard.writeText(justCreated.confirmUrl);
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 1500);
              }}>
                {linkCopied ? t('common.copied') : t('common.copy')}
              </Button>
            ) : null
          }
        >
          {t('dashboard.bookingCreated')}
          {justCreated?.notified?.sms ? ' · 📲' : ''}
          {justCreated?.notified?.email ? ' · 📧' : ''}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
}
